import {lazy, Suspense} from 'react';
import {RouteObject} from 'react-router';
import {Outlet} from 'react-router-dom';
import AuthGuard from 'src/PWM/guards/auth-guard';
import {GuestGuard} from 'src/PWM/guards/guest-guard';
import Error404Page from 'src/PWM/pages/RouteNotFound';
import {PWMPortalLayout} from './layouts/PWMPortalLayout';
import {PWMPublicLayout} from './layouts/PWMPublicLayout';
import VendorAssignmentPage from './pages/contracts/vendorAssignments/VendorAssignmentPage';
import {paths} from './paths';

const authenticatedElements = (
  <AuthGuard>
    <PWMPortalLayout>
      <Suspense>
        <Outlet />
      </Suspense>
    </PWMPortalLayout>
  </AuthGuard>
);

const PWMHome = lazy(() => import('src/PWM/pages/PWMHomePage'));
const ContractListPage = lazy(() => import('src/PWM/pages/contracts/list/ContractListPage'));
const PWMLoginPage = lazy(() => import('./pages/PWMLoginPage'));
const CheckTokenPage = lazy(() => import('./pages/login/CheckTokenScreen'));
const ApplicationSettingsPage = lazy(() => import('./pages/app/SettingsPage'));
const ContractDetailPage = lazy(() => import('./pages/contracts/detail/ContractDetailPage'));
const UserListPage = lazy(() => import('./pages/app/users/list/UserListPage'));
const UserDetailPage = lazy(() => import('./pages/app/users/detail/UserDetailPage'));
const BidListPage = lazy(() => import('./pages/bids/list/BidListPage'));
const BidDetailPage = lazy(() => import('./pages/bids/detail/BidDetailPage'));
const BidCreatePage = lazy(() => import('./pages/bids/new/CreateBidPage'));
const VendorSearchPage = lazy(() => import('./pages/search/VendorSearchPage'));
const VendorDetailPage = lazy(() => import('./pages/vendors/detail/VendorDetailPage'));
const NotificationsListPage = lazy(() => import('./pages/notifications/NotificationListPage'));
const NotificationDetailPage = lazy(() => import('./pages/notifications/NotificationDetailPage'));
const ContractorCertifySubListPage = lazy(() => import( './pages/contractorCertification/ContractorCertifySubListPage'));
const CertificationDetailPage = lazy(() => import( './pages/contracts/certifySubList/detail/CertificationDetailPage'));
const PWMReportsPage = lazy(() => import('./pages/reports/ReportsHome'));

export const PWMRoutes: RouteObject[] = [
  {
    path: paths.authenticated.index,
    element: authenticatedElements,
    children: [
      {
        path: paths.authenticated.home,
        element: <PWMHome />
      }]
  },
  {
    path: paths.authenticated.settings.index,
    element: authenticatedElements,
    children: [{
      index: true,
      element: <ApplicationSettingsPage />
    }]
  },
  {
    path: paths.authenticated.vendors.vendordetails,
    element: authenticatedElements,
    children: [{index: true, element: <VendorDetailPage />}]
  },
  {
    path: paths.authenticated.vendors.index,
    element: authenticatedElements,
    children: [{index: true, element: <VendorSearchPage />}]
  },{
  path: paths.authenticated.app.users,
      element: authenticatedElements,
      children: [
        {index: true, element: <UserListPage/>},
        {path: paths.authenticated.app.userDetail, element: <UserDetailPage/>}]
    },{
    path: paths.authenticated.bids.index,
    element: authenticatedElements,
    children: [
      {
        index: true,
        element: <BidListPage />
      }, {
        path: paths.authenticated.bids.bidnew,
        element: <BidCreatePage />
      }, {
        path: paths.authenticated.bids.biddetail,
        element: <BidDetailPage />
      }
    ]
  },
  {
    path: paths.authenticated.contracts.index,
    element: authenticatedElements,
    children: [
      {
        index: true,
        element: <ContractListPage />
      }, {
        path: paths.authenticated.contracts.contractdetail,
        element: <ContractDetailPage />
      }, {
        path: paths.authenticated.contracts.certifylistdetail,
        element: <CertificationDetailPage />
      },
      {
        path: paths.authenticated.contracts.vendorassignmentdetail,
        element: <VendorAssignmentPage />
      }
    ]
  },
  {
    path: paths.authenticated.contractorCertification.certify,
    element: authenticatedElements,
    children: [
      {index: true, element: <ContractorCertifySubListPage />}] // todo this should be on its own page with minimal sidebar UI
  },
  {
    path: paths.authenticated.notifications.index,
    element: authenticatedElements,
    children: [
      {
        index: true,
        element: <NotificationsListPage />
      },
      {
        path: paths.authenticated.notifications.notificationdetail,
        element: <NotificationDetailPage />
      }
    ]
  },
  {
    path: paths.authenticated.reports.index,
    element: authenticatedElements,
    children: [
      {
        index: true,
        element: <PWMReportsPage />
      }
    ]
  },
  {
    path: paths.public.login.index,
    element:
      (
        <GuestGuard>
          <PWMPublicLayout>
            <Suspense>
              <Outlet />
            </Suspense>
          </PWMPublicLayout>
        </GuestGuard>
      ),
    children:
      [{
        index: true,
        element: <PWMLoginPage />
      }, {
        path: paths.public.login.checkToken,
        element: <CheckTokenPage />
      }]
  },
  {
    path: '*',
    element:
      <Error404Page />
  }
];